// Schema-version migration registry + sequential runner. Per
// spec/tech-arch.md §5.2 + spec/build-plan.md P-021 row.
//
// **`migrations` registry shape.** `Record<number, (state: any) => any>`.
// Keys are FROM versions: `migrations[N]` transforms a state at
// schemaVersion N into a state at schemaVersion N+1. Per Tech Arch §5.2:
// "Migration runs from `state.schemaVersion` up to `CURRENT_VERSION`,
// applying each step." At v1 init the registry is empty (no v0→v1
// migration needed; v1 is the canonical empty AppState seed). Future
// versions add entries keyed by FROM version as the wrapper shape
// evolves.
//
// **`<any, any>` generic relaxation.** Tech Arch §5.2 declares the
// registry as `Record<number, (state: any) => any>`. Migrations
// transform between historical AppState shapes (state at v1 → state at
// v2 → state at v3); typing each step against its source/destination
// schema would require importing every historical AppStateSchema (a
// version-frozen snapshot per version) into this file. The `any` is
// the documented Tech Arch contract — same precedent as D-075 (block
// registry) + D-077 (`blockRegistry` widening) + D-079 (`PreparedBlock`
// render arm). Single eslint-disable at the registry declaration site.
//
// **Runner stamps `schemaVersion: toVersion` after the loop.**
// Migration bodies focus on shape changes; the runner manages the
// version field independently. This way a migration that forgets to
// bump `schemaVersion` doesn't silently leave the state mid-version.
// The persistence layer (Tech Arch §12.2) treats migration failure as
// parse failure — runner throws propagate out to `loadPersistedState`,
// which surfaces the "couldn't load saved data" banner.
//
// **`fromVersion === toVersion` is a no-op fast path.** Returns the
// input by reference (no spread, no schemaVersion stamp). Saves a
// per-boot allocation when the saved data is already at the current
// version (the common case after v1 stabilizes).

/**
 * v1 → v2 (P-037 header redesign). Walks every Email + Template; for any
 * header block, maps the old `options.style` enum (newsletter / events /
 * general) to the new 7-value enum (signal / state-event / big-3 /
 * signature-event / announcement / prospects / new-members) and resets
 * `inputs` to `{}` (the `subhead` input was dropped). Unknown legacy
 * values fall back to `signal` (the default), which renders cleanly
 * even if it doesn't perfectly match the original intent — better than
 * a parse failure that would block the entire AppState load.
 */
const STYLE_V1_TO_V2: Record<string, string> = {
  newsletter: 'signal',
  events: 'big-3',
  general: 'announcement',
}

interface BlockInstanceV1 {
  blockId: string
  options: { style?: string }
  inputs: unknown
  [key: string]: unknown
}

function migrateHeaderBlock(block: BlockInstanceV1): BlockInstanceV1 {
  if (block.blockId !== 'header') return block
  const oldStyle = block.options?.style
  const newStyle: string =
    typeof oldStyle === 'string' && STYLE_V1_TO_V2[oldStyle] !== undefined
      ? STYLE_V1_TO_V2[oldStyle]!
      : 'signal'
  return { ...block, options: { style: newStyle }, inputs: {} }
}

/**
 * v2 → v3 (P-037.16 D-116). Two block-inventory changes from Banks's
 * batched audit:
 * 1. Drop every `member-shoutout` instance from any blocks[] array. The
 *    Block is removed from the registry; any leftover instance would
 *    fail registry lookup at render time.
 * 2. Rename `welcome-new-members` → `member-list`. Per-member field
 *    cleanups: drop `photoUrl` and `partnerLocation`; keep name +
 *    company + location + partnerName. Drop `options.columns` (the new
 *    schema has no columns field — render is fixed at 3-col).
 *
 * Issue-date `options.alignment` is silently stripped on parse by Zod's
 * default strip mode (the new schema is `z.object({})`), so no explicit
 * migration step is needed there.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- per Tech Arch §5.2: migrations transform between historical AppState shapes
function migrateBlocksV2ToV3(blocks: ReadonlyArray<any>): any[] {
  const out: unknown[] = []
  for (const block of blocks) {
    if (block?.blockId === 'member-shoutout') {
      // Drop silently; the Block is no longer in the registry.
      continue
    }
    if (block?.blockId === 'welcome-new-members') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const oldMembers: any[] = Array.isArray(block.inputs?.members) ? block.inputs.members : []
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const newMembers = oldMembers.map((m: any) => {
        const { photoUrl: _photo, partnerLocation: _partnerLoc, ...rest } = m ?? {}
        return rest
      })
      out.push({
        ...block,
        blockId: 'member-list',
        options: {},
        inputs: {
          ...(block.inputs ?? {}),
          members: newMembers,
        },
      })
      continue
    }
    out.push(block)
  }
  return out
}

/**
 * v3 → v4 (P-037.18 D-118). One block-inventory change from Banks's
 * fourth batched audit:
 * 1. Drop every `sponsors-needed-cta` instance from any blocks[] array.
 *    The Block is removed from the registry; any leftover instance
 *    would fail registry lookup at render time.
 *
 * Day-chairs `options.layout` and footer `options.theme` are silently
 * stripped on parse by Zod's default strip mode (the new schemas are
 * `z.object({})`), so no explicit migration step is needed for those.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- per Tech Arch §5.2: migrations transform between historical AppState shapes
function migrateBlocksV3ToV4(blocks: ReadonlyArray<any>): any[] {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return blocks.filter((block: any) => block?.blockId !== 'sponsors-needed-cta')
}

/**
 * v4 → v5 (P-037.20 D-126). No explicit block transform — the
 * `inputs.heading` field is dropped from 5 Blocks (`day-chairs`,
 * `member-list`, `other-notable-events`, `chapter-whatsapp`,
 * `chapter-sponsors`) as part of removing their baked SectionHeader.
 * Users compose a separate `section-heading` Block above any of them
 * when they want a heading. Zod's default strip mode discards the
 * `inputs.heading` key on parse — same precedent as v2 → v3
 * `issue-date.options.alignment` and v3 → v4
 * `day-chairs.options.layout` / `footer.options.theme`. The migration
 * body just stamps `schemaVersion: 5` on every Email + Template; no
 * per-block walk needed.
 */

/**
 * v5 → v6 (P-037.21 D-128). Same Zod-strip pattern as v4 → v5. The
 * `events-list` Block's `options.headerStyle` enum + `eyebrowText` +
 * `state` are all dropped — the baked SubSectionHeader (eyebrow arm)
 * and StateBar (state arm) headers were removed; users compose a
 * `sub-section-heading` Block above events-list when they want a
 * heading (with the optional state-flag affordance via the
 * sub-section-heading's `state` option). StateBar Component deleted
 * in the same pass. Migration body stamps `schemaVersion: 6` only;
 * Zod's default strip handles the field removal at parse time.
 */

/**
 * v6 → v7 (P-037.22 D-130). Block-inventory drop + schema-shape strips
 * from the eighth batched audit:
 * - `other-notable-events` Block dropped — filtered out of every
 *   blocks[] array. Same explicit-filter pattern as v2 → v3
 *   (`member-shoutout` drop) and v3 → v4 (`sponsors-needed-cta` drop);
 *   leftover instances would fail registry lookup at render time.
 * - `event-card-group.options.style` + `options.columns` +
 *   `inputs.eyebrow` all dropped; per-event `ctaLabel` narrowed to the
 *   events-list enum. Zod's default strip handles all of these at the
 *   block-level parse — same precedent as prior batched-audit schema
 *   simplifications.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- per Tech Arch §5.2: migrations transform between historical AppState shapes
function migrateBlocksV6ToV7(blocks: ReadonlyArray<any>): any[] {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return blocks.filter((block: any) => block?.blockId !== 'other-notable-events')
}

/**
 * v7 → v8 (P-037.23 D-131). Drop-and-replace `chapter-sponsors`
 * instances; the new shape doesn't map cleanly from the old shape
 * (name → sponsorName + several dropped fields), so per Banks's
 * "Remove current block" framing the migration filters every existing
 * `chapter-sponsors` block from every blocks[] array. New-shape Block
 * instances added post-v8 are added through the registry's
 * `defaultInputs` (empty sponsors[] + `sponsors: z.array(...).max(8)`),
 * so chapter staff re-populate the sponsor list per Email.
 *
 * The other v7 → v8 changes are Zod-handled at parse time:
 * - `chapter-whatsapp.options.states` dropped — Zod's default strip on
 *   the new empty options schema discards persisted values.
 * - `chapter-whatsapp.inputs.chapterWideGroups` + `inputs.groupsByState`
 *   added with `.default(...)` — Zod populates them on parse when a
 *   persisted instance lacks them.
 * - `event-card-group.events[].imageUrl` widened to `.optional()` —
 *   schema is now wider, so existing persisted state still validates.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- per Tech Arch §5.2: migrations transform between historical AppState shapes
function migrateBlocksV7ToV8(blocks: ReadonlyArray<any>): any[] {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return blocks.filter((block: any) => block?.blockId !== 'chapter-sponsors')
}

/**
 * v9 → v10 (P-037.28 D-136). Block-inventory drop:
 * - `commentary` Block dropped entirely. Split into 4 atomic Blocks
 *   (headline / text / button / author).
 * - `text-block` Block dropped entirely. Same atomic split — no longer
 *   needed.
 *
 * Same explicit-filter pattern as v2 → v3 (`member-shoutout`) /
 * v3 → v4 (`sponsors-needed-cta`) / v6 → v7 (`other-notable-events`) /
 * v7 → v8 (`chapter-sponsors`). Leftover instances would fail registry
 * lookup at render time.
 *
 * Pre-launch chapter has no production data per Banks-confirmed at
 * planning — instances drop cleanly without auto-migration. Staff
 * recomposes "letter"-style emails using the new atomic Blocks
 * (`headline` + `text` + `button` + `author` in whatever order
 * fits).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- per Tech Arch §5.2: migrations transform between historical AppState shapes
function migrateBlocksV9ToV10(blocks: ReadonlyArray<any>): any[] {
  return blocks.filter(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (block: any) => block?.blockId !== 'commentary' && block?.blockId !== 'text-block',
  )
}

/**
 * v10 → v11 (P-037.29 D-137). No explicit block transform — three
 * richText fields are dropped from existing instances:
 * - `day-chairs.inputs.intro` (optional)
 * - `chapter-whatsapp.inputs.description` (required, pre-v11)
 * - `member-list.inputs.body` (optional)
 *
 * Same Zod-strip pattern as v4 → v5 / v5 → v6 / v8 → v9 — the new
 * schemas omit these fields, so Zod's default strip mode discards
 * persisted values on parse. The migration body just stamps
 * `schemaVersion: 11` on every Email + Template; no per-block walk
 * needed.
 *
 * `chapter-whatsapp.description` was REQUIRED pre-v11. Banks-confirmed
 * at planning: pre-launch chapter has no production data — instances
 * drop cleanly. Staff composes a separate `text` Block above
 * chapter-whatsapp when they want description copy.
 */

/**
 * v11 → v12 (P-037.31 D-139). Additive optional `defaultInputs?` on
 * the Template's per-Block-instance shape — Zod's `.optional()` accepts
 * both missing-key and explicit-`undefined` shapes, so pre-v12
 * Templates parse cleanly under the v12 envelope without transform.
 * The migrations[11] entry just stamps `schemaVersion: 12`. Same
 * pass-through pattern as v4 → v5 / v5 → v6 / v8 → v9 / v10 → v11.
 */

/**
 * v12 → v13 (P-037.30 sixth post-Phase-Close §D). `images.columns`
 * field migrates from `options` → `inputs` so chapter staff can pick
 * the layout per-Email. The migration is per-record-kind asymmetric:
 *
 * - **Template instances**: `options.columns` → `defaultInputs.columns`.
 *   Preserves the staff-intended default — Emails created from this
 *   Template post-migration get the same columns value via the D-139
 *   `mergeDefaultInputs` path.
 * - **Email instances**: `options.columns` → `inputs.columns`.
 *   Preserves the rendered look of existing Emails.
 *
 * Then `columns` is stripped from `options`. `images` Blocks without
 * a persisted `options.columns` value (test fixtures, defaults-only)
 * pass through unchanged.
 *
 * This is the first migration whose `blockTransform` differs between
 * `emails` and `templates` — the `makeBlocksMigration` factory takes a
 * single transform applied uniformly, so this migration is built by
 * hand. Same hand-coded shape as the v1 → v2 header migration's full
 * `(state) => ...` signature (pre-factoring) but expressed inline at
 * the registry assignment site for one-off clarity.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- per Tech Arch §5.2: migrations transform between historical AppState shapes
function migrateImagesColumnsInEmail(blocks: AnyBlocks): any[] {
  return blocks.map((block) => {
    if (block?.blockId !== 'images') return block
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const options: any = block.options ?? {}
    if (!('columns' in options)) return block
    const { columns, ...restOptions } = options
    return {
      ...block,
      options: restOptions,
      inputs: { ...(block.inputs ?? {}), columns },
    }
  })
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- per Tech Arch §5.2: migrations transform between historical AppState shapes
function migrateImagesColumnsInTemplate(blocks: AnyBlocks): any[] {
  return blocks.map((block) => {
    if (block?.blockId !== 'images') return block
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const options: any = block.options ?? {}
    if (!('columns' in options)) return block
    const { columns, ...restOptions } = options
    return {
      ...block,
      options: restOptions,
      defaultInputs: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ...((block.defaultInputs as any) ?? {}),
        columns,
      },
    }
  })
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- per Tech Arch §5.2: migrations transform between historical AppState shapes
const migrateV12ToV13: Migration = (state: any) => ({
  ...state,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  emails: (state.emails ?? []).map((email: any) => ({
    ...email,
    schemaVersion: 13,
    blocks: migrateImagesColumnsInEmail(email.blocks ?? []),
  })),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  templates: (state.templates ?? []).map((template: any) => ({
    ...template,
    schemaVersion: 13,
    blocks: migrateImagesColumnsInTemplate(template.blocks ?? []),
  })),
})

/**
 * v13 → v14 (P-047 / D-142). Email gains `subjectLine` + `previewText`
 * for V2.2 CC Push. Per-record-kind asymmetric: only Emails carry the
 * fields (Templates carry no subject/preview state — those are per-
 * Email semantics), so the transform walks `emails[]` to seed the new
 * fields and just stamps `schemaVersion: 14` on `templates[]`.
 *
 * Seed values are empty strings — the same canonical shape every other
 * code path uses (factory, form-empty state, post-Clear). Existing
 * persisted Emails open with empty subject + preview fields after
 * migration; chapter staff types them per-Email before clicking Push
 * (P-048 gates the button on `subjectLine.length > 0`). Pre-existing
 * `subjectLine` / `previewText` values (defensive — should not occur,
 * but `unknown`-typed input could carry anything) pass through
 * unchanged via `?? ''`.
 *
 * Asymmetric same as v12 → v13 (images-columns) — hand-coded because
 * `makeBlocksMigration`'s uniform per-record transform doesn't model
 * the divergence.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- per Tech Arch §5.2: migrations transform between historical AppState shapes
const migrateV13ToV14: Migration = (state: any) => ({
  ...state,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  emails: (state.emails ?? []).map((email: any) => ({
    ...email,
    schemaVersion: 14,
    subjectLine: typeof email.subjectLine === 'string' ? email.subjectLine : '',
    previewText: typeof email.previewText === 'string' ? email.previewText : '',
  })),
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  templates: (state.templates ?? []).map((template: any) => ({
    ...template,
    schemaVersion: 14,
  })),
})

/**
 * P-037.23 D-131 — `makeBlocksMigration` factory. Every entry in the
 * registry has the same shape: walk emails + templates, bump
 * `schemaVersion` to `toVersion`, and (optionally) transform each
 * record's `blocks[]` via a per-version `blockTransform`. Factoring
 * cuts the prior ~110 LOC of cookie-cutter migration bodies + 14
 * `eslint-disable-next-line` plumbing comments to a 1-line registry
 * entry per version.
 *
 * `blockTransform` defaults to identity-pass-through (the no-op pattern
 * used for v4→v5 / v5→v6 / v7→v8-non-chapter-sponsors paths where Zod's
 * default strip handles the actual field-removal at parse time).
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- per Tech Arch §5.2: migrations transform between historical AppState shapes
type AnyBlocks = ReadonlyArray<any>
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Migration = (state: any) => any

function makeBlocksMigration(
  toVersion: number,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- migrations operate on historical block shapes
  blockTransform: (blocks: AnyBlocks) => any[] = (blocks) => [...blocks],
): Migration {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (state: any) => ({
    ...state,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    emails: (state.emails ?? []).map((email: any) => ({
      ...email,
      schemaVersion: toVersion,
      blocks: blockTransform(email.blocks ?? []),
    })),
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    templates: (state.templates ?? []).map((template: any) => ({
      ...template,
      schemaVersion: toVersion,
      blocks: blockTransform(template.blocks ?? []),
    })),
  })
}

export const migrations: Record<number, Migration> = {
  // v1 → v2: header redesign (per-block transform, not the standard map shape)
  1: makeBlocksMigration(2, (blocks: AnyBlocks) => blocks.map(migrateHeaderBlock)),
  // v2 → v3: member-shoutout drop + welcome-new-members rename
  2: makeBlocksMigration(3, migrateBlocksV2ToV3),
  // v3 → v4: sponsors-needed-cta drop
  3: makeBlocksMigration(4, migrateBlocksV3ToV4),
  // v4 → v5: Zod-strip-only (heading-input drop from 5 Blocks)
  4: makeBlocksMigration(5),
  // v5 → v6: Zod-strip-only (events-list headerStyle/eyebrowText/state drop)
  5: makeBlocksMigration(6),
  // v6 → v7: other-notable-events drop + event-card-group flat schema (latter Zod-handled)
  6: makeBlocksMigration(7, migrateBlocksV6ToV7),
  // v7 → v8: chapter-sponsors drop-and-replace + chapter-whatsapp Option C inputs (latter Zod-handled)
  7: makeBlocksMigration(8, migrateBlocksV7ToV8),
  // v8 → v9: Template.name + Email.name constraint relaxation (.min(1) → no min). Pure
  // widening — old persisted state still validates; the bump is convention-only.
  8: makeBlocksMigration(9),
  // v9 → v10: commentary + text-block drop (per-Block transform via explicit filter).
  9: makeBlocksMigration(10, migrateBlocksV9ToV10),
  // v10 → v11: Zod-strip-only (3 richText drops: day-chairs.intro,
  // chapter-whatsapp.description, member-list.body).
  10: makeBlocksMigration(11),
  // v11 → v12: additive optional `defaultInputs?` on Template's
  // EmailBlockInstance. Pass-through — Zod accepts pre-v12 shapes
  // under the v12 envelope without transform.
  11: makeBlocksMigration(12),
  // v12 → v13: images.columns move from options → inputs (per-record-
  // kind asymmetric migration; templates → defaultInputs.columns,
  // emails → inputs.columns). Hand-coded since makeBlocksMigration's
  // single uniform transform doesn't model the asymmetry.
  12: migrateV12ToV13,
  // v13 → v14: Email gains subjectLine + previewText for V2.2 CC Push.
  // Per-record-kind asymmetric (Emails carry the fields; Templates just
  // bump schemaVersion). Hand-coded same as v12→v13.
  13: migrateV13ToV14,
}

/**
 * Sequentially apply migration steps from `fromVersion` (exclusive of
 * the FROM-version transformation; we already are at fromVersion) up to
 * `toVersion` (inclusive — the final state's `schemaVersion`).
 *
 * Throws if a required migration step is missing or if a migration
 * body throws. The persistence layer catches and treats either as a
 * parse failure per Tech Arch §12.2.
 *
 * The `registry` parameter is exposed for tests; production callers
 * use the default (the module-scope `migrations` registry).
 */
export function runMigrations(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- accepts the historical AppState shape; per-call shape is the registry's contract
  state: any,
  fromVersion: number,
  toVersion: number,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- mirrors the module-scope `migrations` registry shape
  registry: Record<number, (state: any) => any> = migrations,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- per the registry's contract
): any {
  if (fromVersion > toVersion) {
    throw new Error(`runMigrations: fromVersion ${fromVersion} > toVersion ${toVersion}`)
  }
  if (fromVersion === toVersion) return state

  let migrated = state
  for (let v = fromVersion; v < toVersion; v++) {
    const step = registry[v]
    if (step === undefined) {
      throw new Error(`runMigrations: no migration registered for version ${v} → ${v + 1}`)
    }
    migrated = step(migrated)
  }
  return { ...migrated, schemaVersion: toVersion }
}
